import React, { useEffect, useState } from 'react'
import { Box, VStack, useColorMode, usePrefersReducedMotion } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { getTokens } from '../../config/colorTokens'

const MotionBox = motion(Box)
const MotionPath = motion.path
const MotionSvg = motion.svg
const MotionVStack = motion(VStack)

const drawEase = [0.65, 0, 0.35, 1]
const logoViewBox = '0 0 642 112'

const logoPaths = [
	{ label: 'P stem', d: 'M18 94V18' },
	{ label: 'P bowl', d: 'M18 18H54C71 18 81 28 81 43C81 59 70 68 53 68H18' },
	{ label: 'R stem', d: 'M99 94V18' },
	{ label: 'R bowl', d: 'M99 18H134C151 18 161 28 161 43C161 58 150 68 133 68H99' },
	{ label: 'R leg', d: 'M132 68L165 94' },
	{ label: 'A frame', d: 'M185 94L219 18L253 94' },
	{ label: 'A bar', d: 'M199 70H239' },
	{ label: 'S sweep', d: 'M337 25C326 16 306 14 292 24C280 34 282 49 295 56C305 62 324 61 334 70C345 80 339 95 324 100C308 105 286 100 275 88' },
	{ label: 'O first', d: 'M443 56C443 78 426 94 403 94C380 94 363 78 363 56C363 34 380 18 403 18C426 18 443 34 443 56Z' },
	{ label: 'O second', d: 'M541 56C541 78 524 94 501 94C478 94 461 78 461 56C461 34 478 18 501 18C524 18 541 34 541 56Z' },
	{ label: 'N frame', d: 'M559 94V18L624 94V18' }
]

const ringPaths = [
	'M66 120C66 64 654 64 654 120C654 176 66 176 66 120Z',
	'M112 120C112 84 608 84 608 120C608 156 112 156 112 120Z'
]

const progressTicks = Array.from({ length: 12 }, (_, index) => index)

const Loader = () => {
	const { colorMode } = useColorMode()
	const shouldReduceMotion = usePrefersReducedMotion()
	const [progress, setProgress] = useState(0)
	const t = getTokens(colorMode)
	const progressRatio = progress / 100
	const activeTickCount = Math.ceil(progress / (100 / progressTicks.length))
	const gridColor = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.055)' : 'rgba(15, 15, 20, 0.055)'
	const sheenColor = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.34)' : 'rgba(255, 255, 255, 0.72)'

	useEffect(() => {
		if (shouldReduceMotion) {
			setProgress(100)
			return undefined
		}

		const startedAt = performance.now()
		let frameId

		const tick = (time) => {
			const duration = 1200
			const elapsed = Math.min(time - startedAt, duration)
			const ratio = elapsed / duration
			const eased = 1 - Math.pow(1 - ratio, 4)

			setProgress(Math.round(eased * 100))

			if (elapsed < duration) {
				frameId = requestAnimationFrame(tick)
			}
		}

		frameId = requestAnimationFrame(tick)
		return () => cancelAnimationFrame(frameId)
	}, [shouldReduceMotion])

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] }
		},
		exit: shouldReduceMotion
			? {
					opacity: 0,
					transition: { duration: 0.2, ease: [0.4, 0, 1, 1] }
			  }
			: {
					opacity: [1, 1, 0],
					transition: {
						duration: 0.68,
						times: [0, 0.74, 1],
						ease: [0.22, 1, 0.36, 1]
					}
			  }
	}

	const curtainVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] }
		},
		exit: shouldReduceMotion
			? {
					opacity: 0,
					transition: { duration: 0.2, ease: [0.4, 0, 1, 1] }
			  }
			: {
					opacity: [1, 1, 0],
					scaleY: [1, 0.08, 0.02],
					transition: {
						duration: 0.68,
						times: [0, 0.74, 1],
						ease: [0.22, 1, 0.36, 1]
					}
			  }
	}

	const stackVariants = {
		hidden: { opacity: 0, y: 10 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
		},
		exit: shouldReduceMotion
			? {
					opacity: 0,
					transition: { duration: 0.18, ease: [0.4, 0, 1, 1] }
			  }
			: {
					y: ['0vh', '-39vh', '-43vh'],
					scale: [1, 0.76, 0.48],
					opacity: [1, 0.9, 0],
					filter: ['blur(0px)', 'blur(0px)', 'blur(5px)'],
					transition: {
						duration: 0.58,
						times: [0, 0.72, 1],
						ease: [0.22, 1, 0.36, 1]
					}
			  }
	}

	return (
		<MotionBox
			variants={containerVariants}
			initial="hidden"
			animate="visible"
			exit="exit"
			h="100vh"
			w="100%"
			bg="transparent"
			overflow="hidden"
			display="flex"
			alignItems="center"
			justifyContent="center"
			position="fixed"
			top={0}
			left={0}
			zIndex={9999}
			style={{ transformOrigin: 'top center' }}
			role="status"
			aria-live="polite"
			aria-label="Loading page content">
			<MotionBox
				variants={curtainVariants}
				position="absolute"
				inset={0}
				bg={t.bg}
				style={{ transformOrigin: 'top center' }}
				aria-hidden="true">
				<MotionBox
					position="absolute"
					inset={0}
					bgImage={`linear-gradient(${gridColor} 1px, transparent 1px), linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`}
					bgSize={{ base: '48px 48px', md: '64px 64px' }}
					bgPosition="center"
					opacity={colorMode === 'dark' ? 0.78 : 0.92}
					style={{
						maskImage: 'linear-gradient(to bottom, transparent 0%, black 24%, black 76%, transparent 100%)',
						WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 24%, black 76%, transparent 100%)'
					}}
					animate={shouldReduceMotion ? undefined : { backgroundPosition: ['center center', 'calc(50% + 64px) calc(50% + 64px)'] }}
					transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
				/>
				<MotionBox
					position="absolute"
					inset={{ base: '-34% -56%', md: '-26% -34%' }}
					bg={`linear-gradient(112deg, transparent 30%, ${t.accentSoft} 43%, ${t.accentGlow} 50%, ${t.accentSoft} 57%, transparent 70%)`}
					filter="blur(18px)"
					opacity={colorMode === 'dark' ? 0.42 : 0.34}
					animate={shouldReduceMotion ? undefined : { x: ['-8%', '8%', '-8%'], opacity: [0.28, 0.48, 0.28] }}
					transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}
				/>
				<Box
					position="absolute"
					inset={0}
					bg={`linear-gradient(to bottom, ${t.bg} 0%, transparent 34%, transparent 66%, ${t.bg} 100%)`}
					opacity={0.76}
				/>
			</MotionBox>
			<MotionVStack
				variants={stackVariants}
				position="relative"
				zIndex={1}
				spacing={{ base: 5, md: 6 }}
				w={{ base: 'min(86vw, 390px)', md: '460px' }}
				align="stretch">
				<MotionBox
					position="relative"
					minH={{ base: '132px', md: '158px' }}
					display="flex"
					alignItems="center"
					justifyContent="center"
					animate={
						shouldReduceMotion
							? undefined
							: {
									filter: [
										'drop-shadow(0 0 0 transparent)',
										`drop-shadow(0 18px 42px ${t.accentGlow})`,
										'drop-shadow(0 0 0 transparent)'
									]
							  }
					}
					transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}>
					<MotionBox
						position="absolute"
						left="50%"
						top="50%"
						w={{ base: '116%', md: '124%' }}
						h={{ base: '92px', md: '110px' }}
						bg={`linear-gradient(90deg, transparent 0%, ${t.accentSoft} 28%, ${t.accentGlow} 50%, ${t.accentSoft} 72%, transparent 100%)`}
						filter="blur(24px)"
						opacity={shouldReduceMotion ? 0.16 : 0.18}
						style={{ translate: '-50% -50%' }}
						animate={shouldReduceMotion ? undefined : { opacity: [0.1, 0.24, 0.1], scaleX: [0.94, 1.04, 0.94] }}
						transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
						pointerEvents="none"
					/>
					<MotionBox
						position="absolute"
						left="50%"
						top="50%"
						w={{ base: '128%', md: '132%' }}
						maxW="620px"
						style={{ translate: '-50% -50%' }}
						opacity={colorMode === 'dark' ? 0.78 : 0.56}
						aria-hidden="true">
						<MotionSvg
							width="100%"
							height="auto"
							viewBox="0 0 720 240"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							focusable="false">
							<defs>
								<linearGradient id="loader-ring-gradient" x1="66" y1="120" x2="654" y2="120" gradientUnits="userSpaceOnUse">
									<stop offset="0%" stopColor={t.accent} stopOpacity="0.1" />
									<stop offset="48%" stopColor={t.accentMid} stopOpacity="0.62" />
									<stop offset="100%" stopColor={t.accentEnd} stopOpacity="0.1" />
								</linearGradient>
							</defs>
							<path d="M152 120H568" stroke={t.textPrimary} strokeWidth="1" strokeOpacity={colorMode === 'dark' ? 0.1 : 0.08} />
							{ringPaths.map((path, index) => (
								<MotionPath
									key={path}
									d={path}
									stroke="url(#loader-ring-gradient)"
									strokeWidth={index === 0 ? 1.25 : 1}
									strokeLinecap="round"
									strokeDasharray={index === 0 ? '12 20' : '4 18'}
									fill="none"
									initial={{ opacity: shouldReduceMotion ? 0.7 : 0, pathLength: shouldReduceMotion ? 1 : 0.4 }}
									animate={
										shouldReduceMotion
											? { opacity: 0.7, pathLength: 1 }
											: { opacity: [0.28, 0.74, 0.28], pathLength: 1, strokeDashoffset: index === 0 ? [0, -128] : [0, 112] }
									}
									transition={{
										opacity: { duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: index * 0.28 },
										pathLength: { duration: 1, ease: drawEase, delay: 0.12 + index * 0.12 },
										strokeDashoffset: { duration: index === 0 ? 5.8 : 6.8, repeat: Infinity, ease: 'linear' }
									}}
								/>
							))}
						</MotionSvg>
					</MotionBox>
					<MotionSvg
						style={{ position: 'relative', zIndex: 2 }}
						width="88%"
						height="auto"
						viewBox={logoViewBox}
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
						focusable="false"
						initial={false}>
						<defs>
							<linearGradient id="loader-wordmark-gradient" x1="0" y1="18" x2="624" y2="94" gradientUnits="userSpaceOnUse">
								<stop offset="0%" stopColor={t.accent} />
								<stop offset="52%" stopColor={t.accentMid} />
								<stop offset="100%" stopColor={t.accentEnd} />
							</linearGradient>
						</defs>
						{logoPaths.map((path) => (
							<path
								key={`${path.label}-guide`}
								d={path.d}
								stroke={t.textPrimary}
								strokeWidth="7.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeOpacity={colorMode === 'dark' ? 0.12 : 0.1}
								fill="none"
							/>
						))}
						{logoPaths.map((path, index) => (
							<MotionPath
								key={path.label}
								d={path.d}
								stroke="url(#loader-wordmark-gradient)"
								strokeWidth="7.5"
								strokeLinecap="round"
								strokeLinejoin="round"
								fill="none"
								initial={{ pathLength: shouldReduceMotion ? 1 : 0, opacity: shouldReduceMotion ? 1 : 0 }}
								animate={{
									pathLength: 1,
									opacity: 1
								}}
								transition={{
									pathLength: { duration: shouldReduceMotion ? 0 : 0.82, delay: shouldReduceMotion ? 0 : index * 0.055, ease: drawEase },
									opacity: { duration: shouldReduceMotion ? 0 : 0.34, delay: shouldReduceMotion ? 0 : index * 0.04 }
								}}
							/>
						))}
					</MotionSvg>
					{!shouldReduceMotion && (
						<MotionBox
							position="absolute"
							inset={{ base: '20% -16%', md: '18% -14%' }}
							bg={`linear-gradient(105deg, transparent 35%, ${sheenColor} 50%, transparent 65%)`}
							filter="blur(10px)"
							opacity={0.16}
							zIndex={3}
							pointerEvents="none"
							animate={{ x: ['-58%', '58%'] }}
							transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 0.65, ease: [0.22, 1, 0.36, 1] }}
						/>
					)}
				</MotionBox>

				<VStack spacing={2} align="center" w="100%">
					<Box
						w={{ base: '82%', md: '360px' }}
						maxW="calc(100% - 24px)"
						h="8px"
						p="1px"
						bg={t.surfaceAlpha}
						border="1px solid"
						borderColor={t.borderSubtle}
						borderRadius="999px"
						boxShadow={`0 18px 48px rgba(0, 0, 0, ${colorMode === 'dark' ? 0.18 : 0.08}), 0 0 24px ${t.accentGlow}`}
						backdropFilter="blur(14px)"
						overflow="hidden"
						position="relative">
						<MotionBox
							h="100%"
							w="100%"
							bgGradient={t.accentGradient}
							borderRadius="inherit"
							style={{ transformOrigin: 'left center' }}
							initial={{ scaleX: 0 }}
							animate={{ scaleX: progressRatio }}
							transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
						/>
						{!shouldReduceMotion && (
							<MotionBox
								position="absolute"
								insetY="-2px"
								left={0}
								w="34%"
								bg={`linear-gradient(90deg, transparent, ${sheenColor}, transparent)`}
								filter="blur(2px)"
								opacity={0.7}
								animate={{ x: ['-130%', '360%'] }}
								transition={{ duration: 1.25, repeat: Infinity, ease: 'easeInOut' }}
							/>
						)}
						<MotionBox
							position="absolute"
							top="50%"
							left={`${progress}%`}
							w="12px"
							h="12px"
							borderRadius="full"
							bg={t.accentEnd}
							boxShadow={`0 0 18px ${t.accentGlow}`}
							style={{ translate: '-50% -50%' }}
							animate={shouldReduceMotion ? undefined : { scale: [0.92, 1.16, 0.92], opacity: [0.72, 1, 0.72] }}
							transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
						/>
					</Box>
					<Box
						w={{ base: '72%', md: '300px' }}
						display="grid"
						gridTemplateColumns="repeat(12, minmax(0, 1fr))"
						gap="5px"
						aria-hidden="true">
						{progressTicks.map((tick) => (
							<MotionBox
								key={tick}
								h="2px"
								borderRadius="999px"
								bg={tick < activeTickCount ? t.accent : t.borderSubtle}
								opacity={tick < activeTickCount ? 0.9 : 0.26}
								animate={shouldReduceMotion || tick >= activeTickCount ? undefined : { opacity: [0.42, 0.95, 0.42] }}
								transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: tick * 0.035 }}
							/>
						))}
					</Box>
				</VStack>
			</MotionVStack>
		</MotionBox>
	)
}

export default Loader
